import * as Sentry from "@sentry/react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthError, User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { isCustomerUser, tenantIdOf } from '@/integrations/supabase/session';
import {
  markOAuthLoginPending,
  recordLogin,
  recordPendingOAuthLogin,
  resetLoginTracking,
} from '@/lib/loginTracking';
import { useNavigate } from 'react-router-dom';
import { APP_CONFIG } from '@/config/app';

interface GoogleSignInOptions {
  returnPath?: string;
  loginHint?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  /**
   * Redirects to Google; the session arrives when the browser lands back on
   * `returnPath` (default /auth). `loginHint` preselects that Google account.
   */
  signInWithGoogle: (options?: GoogleSignInOptions) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  /** `redirectTo` lets the customer portal land back on its own login page. */
  signOut: (options?: { redirectTo?: string }) => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenantIdRef = useRef<string | undefined>(undefined);

  const user = useMemo(() => session?.user ?? null, [session]);

  const clearClientState = useCallback(() => {
    Sentry.setUser(null);
    resetLoginTracking();
    tenantIdRef.current = undefined;
    void supabase.removeAllChannels();
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, next) => {
      // auth-js re-emits SIGNED_IN with a fresh Session object on every tab
      // focus; only a new access token is a change worth re-rendering for.
      setSession((prev) => {
        if (event !== 'USER_UPDATED' && prev?.access_token === next?.access_token) return prev;
        return next;
      });
      setIsLoading(false);

      switch (event) {
        case 'INITIAL_SESSION':
        case 'SIGNED_IN':
          tenantIdRef.current = tenantIdOf(next?.user);
          if (next?.user) recordPendingOAuthLogin(next.user);
          break;
        case 'TOKEN_REFRESHED': {
          const tenantId = tenantIdOf(next?.user);
          if (tenantId !== tenantIdRef.current) {
            tenantIdRef.current = tenantId;
            queryClient.invalidateQueries({ queryKey: ['tenant'] });
          }
          break;
        }
        case 'SIGNED_OUT':
          clearClientState();
          break;
        default:
          break;
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient, clearClientState]);

  useEffect(() => {
    if (user) {
      // Customers are not staff: identify them by id only, never by email.
      Sentry.setUser(isCustomerUser(user) ? { id: user.id } : { id: user.id, email: user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  const signIn = async (email: string, password: string, tenantSlug?: string) => {
    // If tenant slug is provided, validate it exists (for future subdomain routing)
    if (tenantSlug) {
      const { data: tenant, error: tenantError } = await supabase
        .from('companies')
        .select('id')
        .eq('public_key', tenantSlug)
        .single();

      if (tenantError || !tenant) {
        return { error: new Error('Invalid tenant') };
      }
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (!error && data.user) void recordLogin(data.user);
    return { error };
  };

  const signInWithGoogle = async ({ returnPath = '/auth', loginHint }: GoogleSignInOptions = {}) => {
    // Existing staff are matched on their verified email (Supabase links the
    // Google identity automatically). A new account only gets past
    // handle_new_user with an invite ticket booked by the invite page.
    markOAuthLoginPending();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${APP_CONFIG.appUrl}${returnPath}`,
        queryParams: loginHint
          ? { prompt: 'select_account', login_hint: loginHint }
          : { prompt: 'select_account' },
      },
    });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${APP_CONFIG.appUrl}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });

    return { error };
  };

  const signOut = async (options?: { redirectTo?: string }) => {
    await supabase.auth.signOut();
    clearClientState();
    navigate(options?.redirectTo ?? '/auth');
  };

  return (
    <AuthContext.Provider value={{ user, session, signIn, signInWithGoogle, signUp, signOut, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
